import { LoadingButton } from '@mui/lab'
import { Button, Dialog, DialogActions, DialogContent, Box, Typography } from '@mui/material'
import { FC } from 'react'
import { Delete } from '@mui/icons-material'
import { useDialogOpened } from '../../../../utils/hooks/useDialogOpened'
import { useAccountsStore } from '../../../../store/AccountsStore'
import { fullName } from '../../../../domain/Account/AccountDisplay'
import { MainHostedFamilyFollowUp } from './HostedFamillyFollowUpsTab'
import { HostedFamilyFollowUpDtoType } from '../../../../domain/Animal/HostedFamilyFollowUpDtoType'
import { getReadableHostedFamilyFollowUpType } from '../../../../domain/Animal/AnimalDisplay'

interface Props {
  handleClickOnDelete: () => void
  animalName: string | null
  followUp: MainHostedFamilyFollowUp
  allFollowUps: MainHostedFamilyFollowUp[]
  disabled?: boolean
}

export const DeleteFollowUpModal: FC<Props> = ({
  handleClickOnDelete,
  animalName,
  followUp,
  allFollowUps,
  disabled = false,
}) => {
  const { isDialogOpen, openDialog, closeDialog } = useDialogOpened()
  const accountsStore = useAccountsStore()

  // Check if this is an initial placement
  const isInitialPlacement = followUp.type === HostedFamilyFollowUpDtoType.InitialPlacement

  // Get the next follow-up in the chain
  const sortedFollowUps = [...allFollowUps].sort((a, b) => {
    const dateA = new Date(a.date.split('/').reverse().join('-'))
    const dateB = new Date(b.date.split('/').reverse().join('-'))
    return dateA.getTime() - dateB.getTime()
  })

  const currentIndex = sortedFollowUps.findIndex((fu) => fu.created.at === followUp.created.at)
  const previousFollowUp = currentIndex > 0 ? sortedFollowUps[currentIndex - 1] : null
  const nextFollowUp = currentIndex < sortedFollowUps.length - 1 ? sortedFollowUps[currentIndex + 1] : null

  const getLocationDisplay = (followUp: MainHostedFamilyFollowUp | null): string => {
    if (!followUp) return 'Inconnu'

    if (followUp.type === HostedFamilyFollowUpDtoType.ChangeOfHostedFamily) {
      const accountId = followUp.afterAccountId
      const account = accountsStore.members.find((member) => member.id === accountId)
      return account ? fullName(account) : 'Compte introuvable'
    } else {
      const destination = followUp.destination
      if (destination?.startsWith('A-')) {
        const account = accountsStore.members.find((member) => member.id === destination)
        return account ? fullName(account) : 'Compte introuvable'
      }
      return destination || 'Inconnu'
    }
  }

  const getDeleteMessage = (): JSX.Element => {
    if (isInitialPlacement) {
      return (
        <Typography color="error" variant="body1">
          ⚠️ Impossible de supprimer le placement initial. Ce suivi est nécessaire pour maintenir l&apos;historique de
          l&apos;animal.
        </Typography>
      )
    }

    return (
      <Box>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Vous êtes sur le point de supprimer le suivi suivant :
        </Typography>

        <Box sx={{ p: 2, backgroundColor: 'grey.100', borderRadius: 1, mb: 2 }}>
          <Typography variant="body2">
            <strong>Type:</strong> {getReadableHostedFamilyFollowUpType(followUp.type)}
          </Typography>
          <Typography variant="body2">
            <strong>Date:</strong> {followUp.date}
          </Typography>
          {followUp.description && (
            <Typography variant="body2">
              <strong>Description:</strong> {followUp.description}
            </Typography>
          )}
        </Box>

        {previousFollowUp && nextFollowUp ? (
          <Typography variant="body1">
            Après suppression, la chaîne de suivi sera reconstituée automatiquement :
            <br />
            <strong>{getLocationDisplay(previousFollowUp)}</strong> →{' '}
            <strong>{getLocationDisplay(nextFollowUp)}</strong>
          </Typography>
        ) : nextFollowUp ? (
          <Typography variant="body1">Le suivi suivant sera automatiquement lié au placement initial.</Typography>
        ) : (
          <Typography variant="body1">
            {animalName} sera automatiquement rattaché à sa dernière localisation connue :
            <strong>{getLocationDisplay(previousFollowUp)}</strong>
          </Typography>
        )}
      </Box>
    )
  }

  return (
    <>
      <Button
        sx={{ color: 'error.main' }}
        onClick={() => openDialog()}
        disabled={disabled || isInitialPlacement}
        title={isInitialPlacement ? 'Impossible de supprimer le placement initial' : 'Supprimer ce suivi'}
      >
        <Delete />
      </Button>

      <Dialog open={isDialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogContent>
          <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
            Confirmer la suppression
          </Typography>
          {getDeleteMessage()}
        </DialogContent>
        <DialogActions>
          <Button data-testid="cancel" onClick={closeDialog}>
            Annuler
          </Button>
          {!isInitialPlacement && (
            <LoadingButton
              data-testid="apply-changes"
              variant="contained"
              onClick={() => {
                handleClickOnDelete()
                closeDialog()
              }}
              sx={{ backgroundColor: 'error.main' }}
            >
              Supprimer
            </LoadingButton>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}
